import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Select, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material'
import { Copy, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AppShell, Crumb } from '@agribot/polygon-checks-ui'
import {
  useAdminKeys, useAdminOrganisations, useCreateAdminKey, useCreateOrganisation,
  useRevokeAdminKey,
} from './hooks'
import { EmptyState, Eyebrow, FieldLabel, Mono, StatusPill, Surface } from '@agribot/polygon-checks-ui'
import { Segmented } from '@agribot/polygon-checks-ui'
import { SkeletonGridRows } from '@agribot/polygon-checks-ui'
import type { AdminKey } from './types'
import { formatDate, num } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'

const GRID = 'minmax(0,1.5fr) minmax(0,1fr) 150px 80px 110px 110px 90px'

/**
 * The lineage, as a tree.
 *
 * A key is minted by a person, by another key, or by neither — the first
 * platform key comes from a terminal, because the route that issues one
 * already needs one. Nesting on `created_by_key` answers "who created what"
 * directly; a key minted by a person has no key above it, so it sits under
 * that person instead.
 *
 * The API returns minters before what they minted, so one pass builds this.
 */
interface Node { key: AdminKey; children: Node[] }

function lineage(keys: AdminKey[]): { roots: Node[]; orphans: Node[] } {
  const byId = new Map<string, Node>()
  for (const key of keys) byId.set(key.id, { key, children: [] })

  const roots: Node[] = []
  const orphans: Node[] = []
  for (const key of keys) {
    const node = byId.get(key.id)!
    const parent = key.created_by_key ? byId.get(key.created_by_key) : undefined
    if (parent && parent !== node) parent.children.push(node)
    // A key whose minter is not in this list — revoked and filtered out, say
    // — would otherwise vanish. It goes at the top with its parentage named.
    else if (key.created_by_key) orphans.push(node)
    else roots.push(node)
  }
  return { roots, orphans }
}

/** Flattens the tree for rendering, carrying each row's depth. */
function rows(nodes: Node[], depth = 0): { key: AdminKey; depth: number }[] {
  return nodes.flatMap((n) => [{ key: n.key, depth }, ...rows(n.children, depth + 1)])
}

/**
 * Keys, across every organisation.
 *
 * A partner's root key is the credential their integration provisions users
 * with, so this screen answers the two questions that come up when one goes
 * wrong: which organisation does it belong to, and what has it created. The
 * secret is not among them — only its digest was ever stored.
 */
export function Keys() {
  const navigate = useNavigate()
  const [showRevoked, setShowRevoked] = useState(false)
  const { data: keys = [], isLoading } = useAdminKeys(
    showRevoked ? { include_revoked: 'true' } : {},
  )
  const [creating, setCreating] = useState(false)
  const [creatingOrg, setCreatingOrg] = useState(false)
  // Set when the key dialog follows creating an organisation, so the one
  // just made is already chosen.
  const [forOrganisation, setForOrganisation] = useState<string | null>(null)
  const [minted, setMinted] = useState<{ key: AdminKey; secret: string } | null>(null)
  const [revoking, setRevoking] = useState<AdminKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const revoke = useRevokeAdminKey()

  const { roots, orphans } = lineage(keys)
  const ordered = [...rows(orphans), ...rows(roots)]

  const actions = (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Plus size={14} strokeWidth={1.5} />}
        onClick={() => setCreatingOrg(true)}
      >
        New organisation
      </Button>
      <Button
        size="small"
        startIcon={<Plus size={14} strokeWidth={1.5} />}
        onClick={() => { setForOrganisation(null); setCreating(true) }}
      >
        New key
      </Button>
    </>
  )

  return (
    <AppShell crumbs={[<Crumb key="a">Admin</Crumb>, <Crumb key="b">Keys</Crumb>]} actions={actions}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
        <Typography variant="h1" sx={{ mb: 1 }}>Keys</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          Nested by who minted what; open a key to see the users it
          provisioned. A partner root key creates and revokes
          that partner’s users and reaches nothing else; a platform key
          belongs to no organisation and mints the partner keys beneath it.
          Revoking a key does not revoke the users it made — their tokens are
          their own.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowRevoked((v) => !v)}
            sx={{ color: 'text.secondary' }}
          >
            {showRevoked ? 'Hide revoked' : 'Show revoked'}
          </Button>
        </Box>

        <Surface sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 3, px: 5 }}>
            {['Key', 'Organisation', 'Prefix', 'Users', 'Created', 'Last used', ''].map((h, i) => (
              <Box
                key={h || i}
                sx={{
                  display: 'flex', alignItems: 'center', height: 36,
                  justifyContent: i >= 3 && i <= 5 ? 'flex-end' : 'flex-start',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'text.disabled',
                }}
              >
                {h}
              </Box>
            ))}
            {isLoading && <SkeletonGridRows columns={7} rows={5} align={[3, 4, 5]} />}
            {ordered.map(({ key: k, depth }) => (
              <KeyRow
                key={k.id}
                row={k}
                depth={depth}
                onOpen={() => navigate(`/admin/keys/${k.id}`)}
                onRevoke={() => setRevoking(k)}
              />
            ))}
          </Box>

          {!isLoading && keys.length === 0 && (
            <EmptyState
              title="No keys"
              body="Mint one to give a partner access, or to add another platform operator."
              action={
                <Button size="small" startIcon={<Plus size={14} strokeWidth={1.5} />} onClick={() => setCreating(true)}>
                  New key
                </Button>
              }
            />
          )}
        </Surface>
      </Box>

      <NewOrganisationDialog
        open={creatingOrg}
        onClose={() => setCreatingOrg(false)}
        onError={setError}
        // Straight into the key: an organisation without one can do nothing.
        onCreated={(org) => {
          setForOrganisation(org.id)
          setCreating(true)
        }}
      />
      <NewKeyDialog
        open={creating}
        organisationId={forOrganisation}
        onClose={() => setCreating(false)}
        onCreated={setMinted}
        onError={setError}
        onNeedOrganisation={() => {
          setCreating(false)
          setCreatingOrg(true)
        }}
      />
      <SecretDialog minted={minted} onClose={() => setMinted(null)} />
      <RevokeDialog
        target={revoking}
        onClose={() => setRevoking(null)}
        onConfirm={(id) => {
          setRevoking(null)
          revoke.mutate(id, { onError: (e) => setError((e as Error).message) })
        }}
      />
      <Snackbar open={Boolean(error)} autoHideDuration={8000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </AppShell>
  )
}

function KeyRow(
  { row, depth, onOpen, onRevoke }: {
    row: AdminKey; depth: number; onOpen: () => void; onRevoke: () => void
  },
) {
  const revoked = Boolean(row.revoked_at)
  const cell = {
    height: 48, display: 'flex', alignItems: 'center', minWidth: 0,
    borderTop: `1px solid ${tokens.color.ink200}`,
    opacity: revoked ? 0.55 : 1,
  }
  const right = (node: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{node}</Box>
  )

  return (
    <Box
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      role="button"
      tabIndex={0}
      aria-label={`${row.name} — users this key provisioned`}
      sx={{
        display: 'contents',
        '&:hover > *': { backgroundColor: tokens.color.ink50 },
        '& > *': { cursor: 'pointer' },
        '&:focus-visible > *': { backgroundColor: tokens.color.ink100 },
      }}
    >
      <Box sx={{ ...cell, gap: 2, pl: depth * 4 }}>
        {depth > 0 && (
          // A quiet elbow rather than a full guide: the indent carries the
          // structure, this only marks where a child begins.
          <Box
            aria-hidden
            sx={{
              width: 10, height: 10, flex: 'none', ml: -2, mr: 0.5, mt: -1,
              borderLeft: `1px solid ${tokens.color.ink300}`,
              borderBottom: `1px solid ${tokens.color.ink300}`,
              borderBottomLeftRadius: 3,
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{row.name}</Typography>
            {row.scope === 'platform:admin' && <StatusPill status="flagged" label="platform" />}
            {revoked && <StatusPill status="rejected" label="revoked" />}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled' }} noWrap>
            {minter(row)}
          </Typography>
        </Box>
      </Box>
      <Box sx={cell}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {row.organisation_name ?? '—'}
        </Typography>
      </Box>
      <Box sx={cell}>
        <Mono sx={{ fontSize: 12, color: 'text.secondary' }}>{row.prefix}…</Mono>
      </Box>
      {right(<Typography variant="body2">{num(row.users)}</Typography>)}
      {right(
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatDate(row.created_at)}
        </Typography>,
      )}
      {right(
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {row.last_used_at ? formatDate(row.last_used_at) : 'never'}
        </Typography>,
      )}
      <Box sx={{ ...cell, justifyContent: 'flex-end' }}>
        {!revoked && (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => { e.stopPropagation(); onRevoke() }}
          >
            Revoke
          </Button>
        )}
      </Box>
    </Box>
  )
}

/**
 * Where a key came from, in words.
 *
 * The indent already shows a key minted by another key, so that case says
 * nothing; the two that the tree cannot show are a person, and a terminal.
 */
function minter(row: AdminKey): string {
  if (row.created_by_key) return ''
  if (row.created_by) return `Created by ${row.created_by_name ?? 'someone since removed'}`
  return 'Created from a terminal'
}

/**
 * A key, for one organisation or for the platform.
 *
 * The organisation select used to answer three questions at once: which
 * organisation, whether to invent one, and — as "No organisation" — whether
 * this is a platform key at all. The last is not an organisation at all but
 * a privilege tier, and hiding it at the bottom of a list of partners is how
 * somebody issues one by accident. It is now the first thing asked, in its
 * own words, and making an organisation is a separate button on the screen
 * behind this one.
 */
function NewKeyDialog({
  open, organisationId: preset, onClose, onCreated, onError, onNeedOrganisation,
}: {
  open: boolean
  /** Preselected, when this follows creating the organisation. */
  organisationId?: string | null
  onClose: () => void
  onCreated: (minted: { key: AdminKey; secret: string }) => void
  onError: (message: string) => void
  onNeedOrganisation: () => void
}) {
  const { data: organisations = [] } = useAdminOrganisations()
  const create = useCreateAdminKey()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<'users:manage' | 'platform:admin'>('users:manage')
  const [organisationId, setOrganisationId] = useState('')

  // Opened from "New organisation", so the one just made is the answer.
  useEffect(() => {
    if (open && preset) {
      setScope('users:manage')
      setOrganisationId(preset)
    }
  }, [open, preset])

  const partner = scope === 'users:manage'

  const submit = () => {
    create.mutate(
      partner
        ? { name: name.trim(), organisation_id: organisationId }
        : { name: name.trim(), scope: 'platform:admin' },
      {
        onSuccess: (minted) => {
          setName('')
          setOrganisationId('')
          setScope('users:manage')
          onClose()
          onCreated(minted)
        },
        onError: (e) => onError((e as Error).message),
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New key</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 1 }}>
        <Box>
          <FieldLabel>What it is for</FieldLabel>
          <Segmented
            ariaLabel="Key kind"
            value={scope}
            onChange={(v) => setScope(v as typeof scope)}
            options={[
              { value: 'users:manage', label: 'A partner' },
              { value: 'platform:admin', label: 'The platform' },
            ]}
          />
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1.5, display: 'block' }}>
            {partner
              ? 'Creates and revokes one organisation’s users, and nothing else.'
              : 'Issues keys and reads usage across every organisation. No data of its own.'}
          </Typography>
        </Box>

        {partner && (
          <Box>
            <FieldLabel>Organisation</FieldLabel>
            {organisations.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                There are none yet.{' '}
                <Link component="button" type="button" onClick={onNeedOrganisation}>
                  Create one first
                </Link>
                .
              </Typography>
            ) : (
              <Select
                fullWidth size="small" displayEmpty value={organisationId}
                onChange={(e) => setOrganisationId(e.target.value)}
              >
                <MenuItem value="" disabled>Choose one</MenuItem>
                {organisations.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </Select>
            )}
          </Box>
        )}

        <Box>
          <FieldLabel>Name</FieldLabel>
          <TextField
            fullWidth size="small" value={name}
            placeholder="What it is for, and who holds it"
            onChange={(e) => setName(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button
          onClick={submit}
          disabled={!name.trim() || (partner && !organisationId) || create.isPending}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * An organisation, on its own.
 *
 * It can do nothing until it has a key, so the key dialog opens straight
 * afterwards with this one chosen — the onboarding is still one flow, but
 * each step says what it is doing rather than one form doing both silently.
 */
function NewOrganisationDialog({
  open, onClose, onCreated, onError,
}: {
  open: boolean
  onClose: () => void
  onCreated: (organisation: { id: string; name: string }) => void
  onError: (message: string) => void
}) {
  const createOrg = useCreateOrganisation()
  const [name, setName] = useState('')

  const submit = () => {
    createOrg.mutate(
      { name: name.trim() },
      {
        onSuccess: (org) => {
          setName('')
          onClose()
          onCreated(org)
        },
        onError: (e) => onError((e as Error).message),
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New organisation</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <FieldLabel>Name</FieldLabel>
        <TextField
          fullWidth size="small" autoFocus value={name}
          placeholder="The partner’s name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && name.trim() && submit()}
          inputProps={{ 'aria-label': 'Organisation name' }}
        />
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1.5, display: 'block' }}>
          Names fold case, so a partner cannot appear twice. A key comes next:
          an organisation without one can do nothing.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={!name.trim() || createOrg.isPending}>
          {createOrg.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Revoking asks first.
 *
 * It cannot be undone, and it breaks whatever integration is holding that
 * key the moment it lands, which is more than a row in a table should do to
 * a misplaced click.
 */
function RevokeDialog({
  target, onClose, onConfirm,
}: {
  target: AdminKey | null
  onClose: () => void
  onConfirm: (id: string) => void
}) {
  return (
    <Dialog open={Boolean(target)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Revoke {target?.name}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          It stops working immediately and cannot be restored.
          {target?.users
            ? ` The ${target.users} ${target.users === 1 ? 'user' : 'users'} it created keep
               working — their tokens are their own. Revoke those separately if this key leaked.`
            : ''}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button color="error" onClick={() => target && onConfirm(target.id)}>Revoke key</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The secret, once.
 *
 * Only its digest is stored, so there is no second chance to read it and no
 * route that could return it. The dialog says so rather than leaving someone
 * to discover it by closing this and looking for the key in the list.
 */
function SecretDialog({
  minted, onClose,
}: {
  minted: { key: AdminKey; secret: string } | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    if (!minted) return
    await navigator.clipboard.writeText(minted.secret)
    setCopied(true)
  }

  return (
    <Dialog
      open={Boolean(minted)}
      onClose={() => { setCopied(false); onClose() }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{minted?.key.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Copy this now. Only a digest of it is kept, so it cannot be shown
          again — losing it means minting another key.
        </Typography>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, p: 3,
            borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100,
          }}
        >
          <Mono sx={{ fontSize: 13, flex: 1, wordBreak: 'break-all' }}>{minted?.secret}</Mono>
          <Tooltip title={copied ? 'Copied' : 'Copy'}>
            <Button size="small" variant="text" onClick={copy} sx={{ flex: 'none' }}>
              <Copy size={16} strokeWidth={1.5} />
            </Button>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setCopied(false); onClose() }}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
